/**
 * Liore social video rail.
 *
 * Duration formatting, capsule labels and the "Watch on" destination all live
 * in `liore-social-post-card.liquid`. They used to be duplicated here as
 * exported helpers that nothing called, and the two copies had already
 * diverged. The Liquid is the single implementation.
 *
 * The rail itself never contains a YouTube player — it renders posters that
 * belong to us. A player is mounted only inside the lightbox, only when a
 * person taps a card, and it is torn down when the dialog closes.
 *
 * This module deliberately imports nothing from `@theme/*` so that the pure
 * helpers below can be unit tested under `node --test`.
 */

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * Pull a video id out of whatever the merchant pasted.
 *
 * Requiring a bare 11-character id was a trap: a pasted watch/shorts URL
 * failed validation silently and the card fell back to a plain link with no
 * inline playback, giving no indication anything was wrong.
 *
 * @param {string} input - A bare id, or a watch / youtu.be / shorts / embed URL.
 * @returns {string} The id, or '' when there isn't one.
 */
export function youtubeId(input) {
  const raw = String(input ?? '').trim();
  if (!raw) return '';
  if (YOUTUBE_ID.test(raw)) return raw;

  // Anchored to a real parameter: a bare `includes('v=')` also fired on a
  // path segment that happens to contain it, and misrouted the parse.
  const query = /[?&]v=([^&#]+)/.exec(raw);
  const candidate = query ? query[1] : raw.split(/[?#]/)[0].split('/').pop();

  const id = String(candidate ?? '').split(/[?&#/]/)[0];
  return YOUTUBE_ID.test(id) ? id : '';
}

/**
 * Build the privacy-preserving embed URL.
 *
 * Only parameters documented by YouTube are sent: the Requirements for
 * Minimum Functionality forbid changes to the player that the API docs do
 * not describe. `modestbranding` is deliberately absent — it was retired in
 * 2023 and is now undocumented.
 *
 * @param {string} id - An 11-character YouTube video id.
 * @param {{ autoplay?: boolean }} [options]
 * @returns {string} The embed URL, or `''` when the id is not a video id.
 */
export function youtubeEmbedUrl(id, options = {}) {
  const videoId = youtubeId(id);
  if (!videoId) return '';

  const params = new URLSearchParams({
    rel: '0',
    playsinline: '1',
    enablejsapi: '1',
    controls: '1',
  });

  // The player validates inbound JS-API messages against this, so without it
  // `enablejsapi` is decorative and every postMessage command is ignored.
  if (options.origin) params.set('origin', options.origin);

  if (options.autoplay) params.set('autoplay', '1');

  return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
}

/**
 * Hover previews are a desktop affordance and an accessibility hazard if
 * forced. Touch devices have no hover, and motion must be opt-out.
 *
 * @param {{ hasPreview: boolean, coarsePointer: boolean, reducedMotion: boolean }} state
 * @returns {boolean}
 */
export function shouldPlayPreview({ hasPreview, coarsePointer, reducedMotion } = {}) {
  return Boolean(hasPreview) && !coarsePointer && !reducedMotion;
}

/**
 * Whether a playing card has scrolled far enough out of view that its video
 * should stop making noise. Mirrors the RMF's own notion of visibility: a
 * player that is less than half on screen is not really being watched.
 *
 * @param {number} ratio - IntersectionObserver intersectionRatio.
 * @returns {boolean}
 */
/** @returns {boolean} Whether ?socialdebug=1 is on the URL. */
function debugging() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('socialdebug') === '1';
}

const MUTED_KEY = 'liore-social-muted-autoplay';

/**
 * Whether the browser says up front that sound will not be allowed.
 *
 * The Autoplay Policy Detection API answers directly what we otherwise have
 * to discover by waiting to see whether playback started. Where it exists,
 * the first video skips that delay entirely. It reports the policy for this
 * document rather than for the cross-origin frame, so it is a good proxy and
 * not a guarantee: a browser that says "allowed" may still refuse the frame,
 * which is why the timing fallback stays.
 *
 * @param {Navigator} [nav]
 * @returns {boolean}
 */
export function autoplayNeedsMuting(nav) {
  try {
    const policy = (nav ?? navigator).getAutoplayPolicy?.('mediaelement');
    return policy === 'allowed-muted' || policy === 'disallowed';
  } catch {
    return false;
  }
}

/**
 * Whether this browser has already refused autoplay with sound.
 *
 * Safari refuses it for the whole session, so retrying on every card costs a
 * visible stall before each video. Remember the refusal instead of paying it
 * repeatedly. Storage can throw (private mode, blocked site data), and a
 * failure to remember is only a slower path, never a broken one.
 *
 * @returns {boolean}
 */
export function soundRefused(storage) {
  try {
    return (storage ?? window.sessionStorage).getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

/** @param {Storage} [storage] */
export function rememberSoundRefused(storage) {
  try {
    (storage ?? window.sessionStorage).setItem(MUTED_KEY, '1');
  } catch {
    // Not remembering costs a retry next time; it breaks nothing.
  }
}

/**
 * The player state a message carries, or null if it carries none.
 *
 * The API reports state both as `onStateChange` and inside the `infoDelivery`
 * envelope, and `data` is sometimes already an object rather than JSON.
 * 1 is playing; 2 paused; 3 buffering.
 *
 * @param {unknown} data
 * @returns {number | null}
 */
export function playerStateFrom(data) {
  let payload = data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  if (!payload || typeof payload !== 'object') return null;

  const info = /** @type {Record<string, any>} */ (payload).info;
  const state = info && typeof info === 'object' ? info.playerState : info;
  return typeof state === 'number' ? state : null;
}

/**
 * @param {unknown} data
 * @returns {boolean}
 */
export function isPlayingMessage(data) {
  return playerStateFrom(data) === 1;
}

/**
 * How much of `el` is inside the viewport, 0..1, measured now.
 *
 * Needed because the RMF condition is about the player, and the player has
 * no box until the card expands: IntersectionObserver's first callback comes
 * a frame later, which is too late to decide whether autoplay is allowed.
 *
 * @param {Element} el
 * @param {number} [viewportHeight]
 * @returns {number}
 */
export function elementVisibleRatio(el, viewportHeight) {
  const rect = el?.getBoundingClientRect?.();
  if (!rect || !rect.height) return 0;

  const viewport =
    viewportHeight ??
    (typeof window === 'undefined'
      ? 0
      : window.innerHeight || document.documentElement?.clientHeight || 0);
  if (!viewport) return 0;

  const visible = Math.min(rect.bottom, viewport) - Math.max(rect.top, 0);
  return Math.max(0, Math.min(1, visible / rect.height));
}

/**
 * A player must stay more than half visible to keep playing.
 * @param {number} ratio
 * @returns {boolean}
 */
export function shouldPauseForVisibility(ratio) {
  return !(Number(ratio) >= 0.5);
}

/* -------------------------------------------------------------------------
 * Browser wiring
 * ---------------------------------------------------------------------- */

const isBrowser = typeof window !== 'undefined' && typeof HTMLElement !== 'undefined';

if (isBrowser) {
  /**
   * The rail that currently owns playback, across every instance on the page.
   *
   * Per-component state allowed two rails to auto-play simultaneously, which
   * the RMF forbids: at most one automatically playing player per page.
   */
  const active = { rail: null };

/**
 * Scroll locking is page-wide, so it cannot be per-instance state. Two rails
 * both resolve to the same `.page-wrapper`: with the previous value stored on
 * the element, the second lock overwrote the first's saved value, and closing
 * them in the other order restored `overflow: hidden` — leaving the page
 * frozen with every dialog shut.
 */
const scrollLock = { depth: 0, target: null, previous: '' };

  const matches = (query) => window.matchMedia?.(query)?.matches ?? false;

  /**
   * The element that actually scrolls vertically around `el`.
   *
   * Not always the window: this theme puts `overflow: hidden` on html and
   * body and scrolls a `.page-wrapper` instead, so anything that assumes
   * `window.scrollTo` silently does nothing.
   *
   * @param {Element} el
   * @returns {Element|Window}
   */
  /**
   * How far below the top of the viewport content must sit to clear a
   * sticky header. Falls back to a small margin when there isn't one.
   */
  /**
   * How far below the top of the viewport content must sit to clear the
   * sticky header.
   *
   * Shopify wraps the header in a section element that is itself sticky but
   * measures 0px tall — the visible bar is its child. Measuring the wrapper
   * (or probing what is painted, since this header reveals on scroll and is
   * often absent at measure time) both report nothing, which parks the card
   * underneath it.
   */
  const stickyOffset = () => {
    let height = 0;

    for (const header of document.querySelectorAll('header')) {
      const { position } = getComputedStyle(header);
      if (position !== 'sticky' && position !== 'fixed') continue;

      height = Math.max(height, Math.round(header.getBoundingClientRect().height));

      // Zero-height sticky wrapper: the bar people actually see is inside it.
      for (const child of header.children) {
        height = Math.max(height, Math.round(child.getBoundingClientRect().height));
      }
    }

    // Ignore anything implausibly tall — that is a drawer or overlay, not a bar.
    if (height <= 0 || height > window.innerHeight / 2) return 16;
    return height + 12;
  };

  /** Opt-in tracing: append ?socialdebug=1 to the URL. */
  const DEBUG = debugging();
  const trace = (label, data) => {
    if (DEBUG) console.log(`[social-rail] ${label}`, data);
  };

  const verticalScroller = (el) => {
    for (let node = el.parentElement; node; node = node.parentElement) {
      const overflowY = getComputedStyle(node).overflowY;
      if (/auto|scroll|overlay/.test(overflowY) && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
    }
    return window;
  };

  /**
   * The rail. Playback happens inline, in the card that was tapped — there is
   * no dialog. The card widens while it plays so the player clears YouTube's
   * 200px minimum viewport, which a 176px mobile card would not.
   *
   * Only one card may play at a time: the RMF permits a single automatically
   * playing player per page.
   */
  class LioreSocialRail extends HTMLElement {
    #playing = null;
    #pending = null;
    #visibility = null;

    connectedCallback() {
      this.#viewer?.addEventListener('close', this.#onViewerClose);
      this.#viewer?.addEventListener('pointerdown', this.#onViewerPointerDown);
      this.addEventListener('click', this.#onClick);
      this.addEventListener('keydown', this.#onKeyDown);
      this.#track()?.addEventListener('pointerdown', this.#onPointerDown);
      this.addEventListener('dragstart', this.#onDragStart);
      this.#track()?.addEventListener('scroll', this.#syncControls, { passive: true });

      // A ResizeObserver rather than a window resize listener: it also fires
      // when the content changes width — a card widening as it plays, or
      // images finishing load — which a resize event never sees.
      if (typeof ResizeObserver !== 'undefined') {
        this.#resize = new ResizeObserver(this.#syncControls);
        const track = this.#track();
        if (track) {
          this.#resize.observe(track);
          for (const child of track.children) this.#resize.observe(child);
        }
      }

      this.#syncControls();
      this.addEventListener('submit', this.#onAddToCart);
      this.addEventListener('pointerenter', this.#onEnter, true);
      this.addEventListener('pointerleave', this.#onLeave, true);
    }

    disconnectedCallback() {
      this.#stop();
      this.#visibility?.disconnect();
      this.#visibility = null;
      this.#resize?.disconnect();
      this.#resize = null;
      if (active.rail === this) active.rail = null;
      this.#lockScroll(this, false);
      this.#viewer?.removeEventListener('close', this.#onViewerClose);
      this.#viewer?.removeEventListener('pointerdown', this.#onViewerPointerDown);
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('keydown', this.#onKeyDown);
      this.removeEventListener('submit', this.#onAddToCart);
      this.removeEventListener('pointerenter', this.#onEnter, true);
      this.removeEventListener('pointerleave', this.#onLeave, true);
    }

    /**
     * Show the arrows only when there is somewhere to go, and disable each
     * one at its end of the track.
     */
    #syncControls = () => {
      const track = this.#track();
      const controls = this.querySelector('[data-social-controls]');
      if (!track || !controls) return;

      const scrollable = track.scrollWidth - track.clientWidth;
      const canScroll = scrollable >= 8;

      controls.hidden = !canScroll;
      if (!canScroll) return;

      const prev = controls.querySelector('[data-social-prev]');
      const next = controls.querySelector('[data-social-next]');
      // A tolerance, because scrollLeft is fractional on zoomed displays.
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= scrollable - 1;
    };

    /** One card plus its gap, so a click advances by a predictable amount. */
    #step(direction) {
      const track = this.#track();
      if (!track) return;

      // A resting card, never the widened playing one, and the rail's actual
      // gap rather than a hardcoded pixel guess.
      const card = track.querySelector('.liore-social-post-card:not([data-playing])');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const width = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;

      track.scrollBy({
        left: direction * width,
        behavior: matches('(prefers-reduced-motion: reduce)') ? 'auto' : 'smooth',
      });
    }

    /** The scrolling element itself, inside this component. */
    #track() {
      return this.querySelector('.liore-social__rail');
    }

    #drag = null;
    #returnFocusTo = null;

    #resize = null;
    #holdsScrollLock = false;
    #backdropPress = false;

    /**
     * Click-and-drag panning for mouse users.
     *
     * Touch is left alone: it already has native momentum scrolling, and
     * taking it over would replace something better with something worse.
     */
    #onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      const track = this.#track();
      if (!track || track.scrollWidth <= track.clientWidth) return;

      this.#drag = {
        startX: event.clientX,
        startScroll: track.scrollLeft,
        moved: false,
        snap: track.style.scrollSnapType,
      };

      // Capture is deliberately NOT taken here. Capturing on every mousedown
      // makes the browser dispatch the following `click` to the capturing
      // element instead of the button under the cursor, which silently broke
      // every control inside the rail. It is taken once a real drag starts.
      track.addEventListener('pointermove', this.#onPointerMove);
      track.addEventListener('pointerup', this.#onPointerUp);
      track.addEventListener('pointercancel', this.#onPointerUp);
    };

    #onPointerMove = (event) => {
      const drag = this.#drag;
      const track = this.#track();
      if (!drag || !track) return;

      const delta = event.clientX - drag.startX;

      // Below the threshold this is still a click, not a drag.
      if (!drag.moved && Math.abs(delta) < 5) return;

      if (!drag.moved) {
        drag.moved = true;
        track.setPointerCapture?.(event.pointerId);
        // Snapping fights a drag in progress; it is restored on release so
        // the rail still settles onto a card.
        track.style.scrollSnapType = 'none';
        this.dataset.dragging = '';
      }

      track.scrollLeft = drag.startScroll - delta;
    };

    #onPointerUp = (event) => {
      const drag = this.#drag;
      const track = this.#track();
      this.#drag = null;
      if (!track) return;

      if (drag?.moved) track.releasePointerCapture?.(event.pointerId);
      track.removeEventListener('pointermove', this.#onPointerMove);
      track.removeEventListener('pointerup', this.#onPointerUp);
      track.removeEventListener('pointercancel', this.#onPointerUp);
      delete this.dataset.dragging;

      if (!drag?.moved) return;

      track.style.scrollSnapType = drag.snap;

      // Swallow the click this drag would otherwise fire, so releasing over
      // a card does not open the video you were only scrolling past.
      const swallow = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      this.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(() => this.removeEventListener('click', swallow, { capture: true }), 0);
    };

    /** Stop the browser's native link and image dragging. */
    #onDragStart = (event) => {
      if (this.#track()?.contains(event.target)) event.preventDefault();
    };

    #onClick = (event) => {
      const stepper = event.target.closest('[data-social-prev], [data-social-next]');
      if (stepper) {
        event.preventDefault();
        this.#step(stepper.hasAttribute('data-social-prev') ? -1 : 1);
        return;
      }

      if (event.target.closest('[data-social-viewer-close]')) {
        event.preventDefault();
        this.#viewer?.close();
        return;
      }

      // Backdrop dismissal. The dialog has no padding and its children fill
      // it, so the dialog element is only ever the target for a click on the
      // backdrop itself.
      //
      // Guarded by where the press STARTED: a `click` fires on the nearest
      // common ancestor of the press and the release, so dragging from the
      // image out past its edge would otherwise report the dialog and dismiss
      // a viewer the person was interacting with.
      if (event.target === this.#viewer) {
        const fromBackdrop = this.#backdropPress;
        this.#backdropPress = false;
        if (fromBackdrop) this.#viewer?.close();
        return;
      }

      if (event.target.closest('[data-social-stop]')) {
        event.preventDefault();
        this.#stop();
        return;
      }

      // The product chip and any link inside the panel keep their own target.
      if (event.target.closest('[data-social-chip], [data-social-panel] a')) return;

      const trigger = event.target.closest('[data-social-open]');
      if (!trigger) return;

      const card = trigger.closest('[data-social-card]');
      if (!card) return;

      // Anything we can open in place takes over the tap; otherwise let the
      // link go to the original post.
      if (!this.#canOpen(card)) return;

      event.preventDefault();

      // A still opens full size; video plays where it sits.
      if (card.dataset.format === 'image') {
        this.#openViewer(card, trigger.dataset.caption || '');
        return;
      }

      this.#play(card, trigger.dataset.caption || 'Video');
    };

    /** @param {PointerEvent} event */
    #onViewerPointerDown = (event) => {
      this.#backdropPress = event.target === this.#viewer;
    };

    /**
     * The lightbox gets Escape natively from <dialog>; inline playback had no
     * keyboard exit at all, only the Close button.
     * @param {KeyboardEvent} event
     */
    #onKeyDown = (event) => {
      if (event.key !== 'Escape' || !this.#playing) return;
      event.stopPropagation();
      this.#stop();
    };

    /** @param {Element} card */
    #canOpen(card) {
      const { format, youtubeId: id, video, image } = card.dataset;
      if (format === 'image') return Boolean(image);
      if (format === 'video') return Boolean(video);
      return Boolean(youtubeEmbedUrl(id));
    }

    /**
     * Build what belongs in the stage for this card's format.
     *
     * Only the YouTube branch carries the RMF constraints — a self-hosted
     * video or an image is our own element and none of those rules apply.
     *
     * @returns {Element|null}
     */
    #mount(card, title, slot) {
      const { format, youtubeId: id, video, image } = card.dataset;

      if (format === 'video') {
        const el = document.createElement('video');
        el.src = video;
        el.controls = true;
        el.autoplay = true;
        el.playsInline = true;
        el.preload = 'metadata';
        if (image) el.poster = image;
        return el;
      }

      // Start via the documented `autoplay` parameter rather than a
      // postMessage command. The command path cannot work: it can only run
      // after the iframe's async `load`, by which point the tap that
      // authorises unmuted playback has expired, and Safari refuses it.
      //
      // "an API Client must not initiate an automatic playback until the
      // player is visible and more than half of the player is visible".
      //
      // That is about the PLAYER, and the player has no box until the card
      // expands: tapping a card at the bottom of the rail grows it from
      // ~250px to ~700px, and the new player can land below the fold. So the
      // slot is measured here — after [data-playing] has been set and laid
      // out — rather than assumed from the fact that a person tapped.
      //
      // Below half, the frame is mounted idle and `#watchVisibility` starts
      // it once the centring scroll brings it into view, which is the
      // API-initiated path the same clause permits.
      const onScreen = !shouldPauseForVisibility(elementVisibleRatio(slot));
      const src = youtubeEmbedUrl(id, {
        autoplay: onScreen,
        origin: window.location.origin,
      });

      // A browser that already refused sound this session gets the muted
      // version immediately, rather than stalling on the same failed attempt
      // before every single video.
      const muted = soundRefused() || autoplayNeedsMuting();
      if (!src) return null;

      const frame = document.createElement('iframe');
      frame.src = muted ? `${src}&mute=1` : src;
      frame.title = title;
      frame.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture; web-share';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';

      // Without this handshake the player never registers a listener and
      // subsequent `postMessage` commands are dropped, so pause-on-scroll
      // silently did nothing and audio kept playing off screen.
      frame.addEventListener('load', () => {
        frame.contentWindow?.postMessage(
          JSON.stringify({ event: 'listening', id: 'liore-social-rail', channel: 'widget' }),
          'https://www.youtube-nocookie.com',
        );

        if (debugging()) {
          const rect = frame.getBoundingClientRect();
          console.info('[social-rail] player mounted', {
            src: frame.src,
            size: `${Math.round(rect.width)}x${Math.round(rect.height)}`,
            hasBeenActive: navigator.userActivation?.hasBeenActive,
            isActive: navigator.userActivation?.isActive,
          });
        }

      });

      if (onScreen) {
        if (!muted) this.#ensureStarted(card, frame, src);
      } else {
        // Nothing starts it yet; the observer will, once it is really visible.
        this.#pending = { card, frame, src, muted };
      }
      return frame;
    }

    /**
     * Safari refuses autoplay with sound in a cross-origin frame unless it
     * already trusts the site, so the same markup that plays in Chrome can
     * sit there silently. Rather than guess which browser allows what, watch
     * for the player actually reporting PLAYING and, if it never does, reload
     * muted — an autoplay every browser permits. Sound is preferred, but a
     * video that plays muted beats one that does not play.
     *
     * @param {Element} card
     * @param {HTMLIFrameElement} frame
     * @param {string} src
     */
    #ensureStarted(card, frame, src) {
      let everPlayed = false;
      let state = null;

      const onMessage = (event) => {
        if (event.source !== frame.contentWindow) return;
        const next = playerStateFrom(event.data);
        if (next === null) return;
        state = next;
        if (next === 1) everPlayed = true;
      };
      window.addEventListener('message', onMessage);

      const done = () => window.removeEventListener('message', onMessage);
      const alive = () => this.#playing === card && frame.isConnected;

      let waits = 0;
      const decide = () => {
        if (everPlayed || !alive()) return done();

        // State 3 is buffering, not refusal. On a slow connection a first
        // video is often still buffering at the deadline, and treating that
        // as "sound was refused" muted every video for the rest of the
        // session with no way to clear it. Give buffering more time instead.
        if (state === 3 && waits < 3) {
          waits += 1;
          return void window.setTimeout(decide, 1200);
        }

        // The player is loaded and idle, so ask it to start muted over the
        // API. Re-navigating the frame to a `&mute=1` URL starts it too, but
        // it is a visible reload of the video the person just tapped.
        if (debugging()) console.info('[social-rail] no playback; starting muted');
        rememberSoundRefused();
        this.#command(card, 'mute');
        this.#command(card, 'playVideo');

        // Last resort: if even that did nothing, the command channel is not
        // working and a muted reload is all that is left.
        window.setTimeout(() => {
          if (!alive()) return done();
          if (state !== 1) {
            if (debugging()) console.info('[social-rail] commands ignored; reloading muted');
            frame.src = `${src}&mute=1`;
          }
          done();
        }, 1200);
      };

      window.setTimeout(decide, 1200);
    }

    get #viewer() {
      return this.querySelector('[data-social-viewer]');
    }

    /**
     * Show an image post at full size, with its product panel beside it.
     *
     * The panel is cloned rather than rebuilt, so the markup and the
     * add-to-cart form stay in one place. The dialog lives inside this
     * element, so the rail's delegated submit handler still reaches it.
     */
    #openViewer(card, caption) {
      const dialog = this.#viewer;
      if (!dialog || dialog.open) return;

      this.#stop(); // Never leave a video running behind the overlay.

      const image = dialog.querySelector('[data-social-viewer-image]');
      if (image) {
        image.src = card.dataset.image ?? '';
        image.alt = caption;
      }

      const eyebrow = dialog.querySelector('[data-social-viewer-eyebrow]');
      if (eyebrow) {
        eyebrow.textContent = card.querySelector('.liore-social-post-card__capsule')?.textContent ?? '';
      }

      const aside = dialog.querySelector('[data-social-viewer-aside]');
      const panel = card.querySelector('[data-social-panel]');
      if (aside) {
        aside.replaceChildren();
        if (panel) aside.append(panel.cloneNode(true));
      }

      // Remember what opened it, so focus goes back there on close rather
      // than falling to the rail — which is focusable and left a ring
      // outlining the whole row until the next click.
      this.#returnFocusTo = card.querySelector('[data-social-open]');

      // From the component, not the card: `.liore-social__rail` sets
      // overflow-x, which forces computed overflow-y to `auto`, so walking up
      // from the card can lock the rail and leave the page scrolling.
      this.#lockScroll(this, true);
      dialog.showModal();
    }

    #onViewerClose = () => this.#closeViewer();

    #closeViewer() {
      const dialog = this.#viewer;
      if (!dialog) return;

      dialog.querySelector('[data-social-viewer-aside]')?.replaceChildren();
      const image = dialog.querySelector('[data-social-viewer-image]');
      if (image) image.removeAttribute('src');

      this.#lockScroll(this, false);

      const target = this.#returnFocusTo;
      this.#returnFocusTo = null;
      target?.focus({ preventScroll: true });
    }

    /**
     * Freeze the page behind the overlay.
     *
     * Toggles overflow on whatever actually scrolls — this theme puts
     * overflow:hidden on html and body and scrolls a wrapper instead, so
     * locking the body would do nothing at all.
     */
    #lockScroll(node, on) {
      if (!on) {
        // Never re-derive the scroller here: locking sets `overflow: hidden`
        // on it, so it no longer passes the "is scrollable" test and the
        // unlock would land on the wrong element, leaving the page frozen.
        if (!this.#holdsScrollLock) return;
        this.#holdsScrollLock = false;

        scrollLock.depth -= 1;
        // Only the last holder restores, and it restores the value saved by
        // the FIRST one — the state before any dialog was open.
        if (scrollLock.depth > 0 || !scrollLock.target) return;

        scrollLock.target.style.overflow = scrollLock.previous;
        scrollLock.target = null;
        scrollLock.previous = '';
        return;
      }

      if (this.#holdsScrollLock) return;

      const scroller = verticalScroller(node);
      const target = scroller === window ? document.documentElement : scroller;

      this.#holdsScrollLock = true;
      scrollLock.depth += 1;
      if (scrollLock.depth === 1) {
        scrollLock.target = target;
        scrollLock.previous = target.style.overflow;
      }
      target.style.overflow = 'hidden';
    }

    #play(card, title) {
      if (this.#playing === card) return;
      this.#stop();
      if (active.rail && active.rail !== this) active.rail.stopPlayback();

      const slot = card.querySelector('[data-social-player]');
      if (!slot) return;

      // The poster anchor the user just activated is about to be
      // `display: none`d by the [data-playing] rules, which drops focus to
      // <body> and restarts tabbing from the top of the document.
      const wasFocused = card.contains(document.activeElement);

      // Reveal the player box BEFORE inserting the frame. The box is
      // `display: none` until [data-playing] is set, and an iframe inserted
      // into a display:none subtree has no layout box — browsers refuse
      // autoplay to a frame with no size, so inserting first meant the very
      // first video never started. Later taps only appeared to work because
      // the origin had by then earned a media-engagement allowance.
      card.dataset.playing = '';
      this.dataset.anyPlaying = '';

      // Built only now, so it can measure a slot that actually has a box.
      // Guarded: the card is already revealed at this point, and its player
      // box is solid black, so anything thrown in here leaves a black
      // rectangle where the video should be. Fail back to the poster
      // instead — that is how a ReferenceError in the local-video branch
      // showed up as "click does nothing but go black".
      let node = null;
      try {
        node = this.#mount(card, title, slot);
      } catch (error) {
        console.error('[social-rail] could not build the player', error);
      }
      if (!node) {
        delete card.dataset.playing;
        delete this.dataset.anyPlaying;
        return;
      }

      slot.replaceChildren(node);
      // Only once we know playback actually started, so the registry can
      // never pin a rail whose #playing is null.
      active.rail = this;
      if (wasFocused) card.querySelector('[data-social-stop]')?.focus({ preventScroll: true });
      this.#playing = card;
      this.#watchVisibility(card);

      // Scroll the rail itself, never the document. `scrollIntoView` walks
      // up every scrollable ancestor, so a card that grows taller drags the
      // whole page down with it.
      this.#centreInRail(card);
      this.#centreOnScreen(card);
    }

    /**
     * Bring the playing card to the middle of the viewport.
     *
     * Deliberate and vertical-only — unlike `scrollIntoView`, which scrolled
     * the page as a side effect of the card growing and left it off-centre.
     * Deferred until the widening has settled: on mobile the player's height
     * follows the card's width, so measuring sooner centres the wrong box.
     */
    #centreOnScreen(card) {
      const reduced = matches('(prefers-reduced-motion: reduce)');

      if (reduced) {
        this.#alignCard(card, false);
        return;
      }

      this.#alignCard(card, true);
      this.#holdAlignment(card);
    }

    /**
     * Scroll so the card sits where it should, and report how far off it was.
     *
     * @param {Element} card
     * @param {boolean} smooth
     * @returns {number}
     */
    #alignCard(card, smooth, tolerance = 1) {
      if (this.#playing !== card) return 0; // Stopped or replaced meanwhile.

      const scroller = verticalScroller(card);
      const box = card.getBoundingClientRect();
      const frame =
        scroller === window
          ? { top: 0, height: window.innerHeight }
          : scroller.getBoundingClientRect();

      // What is actually on screen, which on iOS is shorter than the scroll
      // container: Safari's address bar overlays it, so the container's own
      // box overstates the visible area and everything lands too low.
      const vv = window.visualViewport;
      const visibleTop = Math.max(frame.top, 0);
      const visibleHeight = Math.min(frame.height, vv ? vv.height : window.innerHeight);

      // Top-align only when centring would clip the card — i.e. it cannot
      // fit between the sticky header and the bottom of the screen. A ratio
      // was wrong here: a ~580px desktop card tripped a 70% threshold on any
      // window under ~830px and lost the centring that reads better there.
      // Phones always take this branch because the card fills the screen.
      const offset = stickyOffset();
      const alignTop =
        matches('(max-width: 749px)') || box.height + offset + 16 > visibleHeight;

      const delta = alignTop
        ? box.top - (visibleTop + offset)
        : box.top + box.height / 2 - (visibleTop + visibleHeight / 2);

      trace('align', {
        scroller: scroller === window ? 'window' : `${scroller.tagName}.${scroller.className}`,
        cardTop: Math.round(box.top),
        cardHeight: Math.round(box.height),
        visibleTop: Math.round(visibleTop),
        visibleHeight: Math.round(visibleHeight),
        stickyOffset: offset,
        alignTop,
        delta: Math.round(delta),
        scrollerTop: scroller === window ? window.scrollY : Math.round(scroller.scrollTop),
      });

      if (Math.abs(delta) > tolerance) {
        scroller.scrollBy({ top: delta, behavior: smooth ? 'smooth' : 'auto' });
      }
      return delta;
    }

    /**
     * Keep re-asserting the position for a short window after play starts.
     *
     * The player scrolls itself into view once it mounts and takes focus,
     * which lands on the video's top edge rather than the card's and hides
     * the stop control. That happens at an unpredictable moment, so hold the
     * intended position rather than racing it with a single timer.
     *
     * Yields immediately to a real scroll gesture — never fight the person.
     */
    #holdAlignment(card) {
      const started = Date.now();
      let timer = 0;
      let cancelled = false;

      // Corrections used to fire every 200ms against a 1px threshold, which
      // meant they interrupted their own in-flight smooth scroll with hard
      // jumps — the stepped, robotic quality. Wait for the smooth scroll to
      // finish, then only correct a drift big enough to actually notice.
      const SETTLE_MS = 700;
      const TOLERANCE_PX = 24;

      const release = () => {
        // A flag as well as the id: a gesture delivered before `timer` is
        // assigned would otherwise clear 0 and leave the interval running.
        cancelled = true;
        window.clearInterval(timer);
        window.removeEventListener('touchstart', release, true);
        window.removeEventListener('wheel', release, true);
      };

      window.addEventListener('touchstart', release, { capture: true, once: true });
      window.addEventListener('wheel', release, { capture: true, once: true });

      timer = window.setInterval(() => {
        if (cancelled) return release();
        const elapsed = Date.now() - started;
        if (this.#playing !== card || elapsed > 2400) {
          release();
          return;
        }
        if (elapsed < SETTLE_MS) return;

        // Measure first; only scroll if it is genuinely off.
        const drift = this.#alignCard(card, false, TOLERANCE_PX);
        if (Math.abs(drift) <= TOLERANCE_PX) release();
      }, 400);
    }

    /**
     * Horizontal-only scroll, confined to the rail's own scroll container.
     *
     * Run twice: once immediately, and again once the card has finished
     * widening. The card's width animates, which moves every snap point in
     * the track while the browser is still settling — landing the card
     * part-way off the left edge.
     *
     * @param {Element} card
     * @param {boolean} [settle] - Schedule the follow-up pass.
     */
    #centreInRail(card, settle = true) {
      const rail = card.parentElement;
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;

      if (settle) {
        window.setTimeout(() => {
          if (this.#playing === card) this.#centreInRail(card, false);
        }, 460);
      }

      // Land the card at the rail's own gutter rather than centring it: the
      // cards now snap to their start edge, and centring fought that while
      // also pushing whatever sat to the left hard against the screen.
      const gutter = parseFloat(getComputedStyle(rail).paddingInlineStart) || 0;
      const target = card.offsetLeft - gutter;
      const left = Math.max(0, Math.min(target, rail.scrollWidth - rail.clientWidth));

      // The first pass animates; the settle pass is authoritative and lands
      // instantly. A smooth scroll can be overridden mid-flight by snap
      // re-evaluating once the card has finished widening, which left the
      // card part-way off the left edge.
      if (settle && !matches('(prefers-reduced-motion: reduce)')) {
        rail.scrollTo({ left, behavior: 'smooth' });
      } else {
        rail.scrollLeft = left;
      }
    }

    #watchVisibility(card) {
      this.#visibility?.disconnect();
      if (typeof IntersectionObserver === 'undefined') return;

      // A grace period, not a visibility gate. Requiring the card to reach
      // 50% first made pause unreachable on short screens: on a 568x320
      // landscape phone a playing card is ~670px tall, so its ratio tops out
      // around 0.48 and the video would keep playing off-screen forever.
      const settled = Date.now() + 1200;

      // The RMF is written about the player, not its container: playback must
      // not begin "until the player is visible and more than half of the
      // player is visible on the page or screen". Watching the card measured
      // the wrong box — a card is far taller than its player because of the
      // product panel, so a card sitting half on screen can have its player
      // entirely off it. Watching the player also retires the reason the
      // grace period above exists: a ~200px player reaches 50% on screens
      // where a ~670px card never could.
      const watched = card.querySelector('[data-social-player]') ?? card;

      this.#visibility = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!shouldPauseForVisibility(entry.intersectionRatio)) {
              // Now more than half the player is on screen, so the deferred
              // start becomes legitimate. This only ever starts a player that
              // has never played — it never resumes one the viewer paused,
              // which is what the Developer Policies prohibit.
              this.#startPending(card);
              continue;
            }
            // Pause regardless of who started it, but not while the opening
            // scroll and widening are still settling.
            if (Date.now() >= settled) this.#pause(card);
          }
        },
        { threshold: [0, 0.25, 0.5, 1] },
      );
      this.#visibility.observe(watched);
    }

    /**
     * Start a player that was mounted idle because it was not yet visible.
     * @param {Element} card
     */
    #startPending(card) {
      const pending = this.#pending;
      if (!pending || pending.card !== card || this.#playing !== card) return;
      if (!pending.frame.isConnected) return;

      this.#pending = null;
      this.#command(card, 'playVideo');
      if (!pending.muted) this.#ensureStarted(card, pending.frame, pending.src);
    }

    /**
     * Pause rather than tear down, so scrolling back finds the video where it
     * was. It is not resumed automatically: restarting playback for someone
     * who did not ask is exactly what the Developer Policies prohibit.
     */
    #pause(card) {
      const local = card.querySelector('[data-social-player] video');
      if (local) {
        local.pause();
        return;
      }
      this.#command(card, 'pauseVideo');
    }


    /** @param {Element} card @param {string} func @param {unknown[]} [args] */
    #command(card, func, args = []) {
      const frame = card.querySelector('iframe');
      frame?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func, args }),
        'https://www.youtube-nocookie.com',
      );
    }

    /** Public so a sibling rail can stop this one. */
    stopPlayback() {
      this.#stop();
    }

    #stop() {
      const card = this.#playing;
      if (!card) return;
      if (active.rail === this) active.rail = null;
      this.#playing = null;
      this.#pending = null;
      this.#visibility?.disconnect();
      this.#visibility = null;

      // The Close button is about to be un-rendered by the [data-playing]
      // rules; without this, focus drops to <body> and tabbing restarts from
      // the top of the document.
      const wasFocused = card.contains(document.activeElement);

      // Removing the iframe ends playback and drops the third-party frame.
      card.querySelector('[data-social-player]')?.replaceChildren();
      delete card.dataset.playing;
      delete this.dataset.anyPlaying;
      if (wasFocused) card.querySelector('[data-social-open]')?.focus({ preventScroll: true });
      this.#resetCart(card);
    }

    #resetCart(card) {
      const form = card.querySelector('[data-social-add]');
      if (form) {
        form.hidden = false;
        form.querySelector('button')?.removeAttribute('disabled');
      }
      const added = card.querySelector('[data-social-added]');
      const error = card.querySelector('[data-social-error]');
      const cartLink = card.querySelector('[data-social-cart-link]');
      if (added) added.hidden = true;
      if (error) error.hidden = true;
      if (cartLink) cartLink.hidden = true;
    }

    #onAddToCart = async (event) => {
      const form = event.target.closest('[data-social-add]');
      if (!form) return;
      event.preventDefault();

      // Scope to the panel, not the card: the viewer clones the panel into
      // the dialog, where there is no ancestor card and every lookup below
      // would silently resolve to undefined.
      const scope = form.closest('[data-social-panel]') ?? form.parentElement;
      const button = form.querySelector('button');
      const id = Number(form.querySelector('input[name="id"]')?.value);
      if (!id) return;

      button?.setAttribute('disabled', '');
      try {
        // Bare `Theme`, not `window.Theme`: scripts.liquid declares it with
        // `const` in a classic script, which creates a declarative binding
        // rather than a property of window — `window.Theme` is always
        // undefined and this silently fell back on every market.
        const routes = (typeof Theme !== 'undefined' && Theme?.routes) || {};
        const endpoint = routes.cart_add_url || '/cart/add.js';
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          // `_source` marks the cart line (hidden from shoppers) so orders show
          // the product was added from the social reel.
          body: JSON.stringify({ items: [{ id, quantity: 1, properties: { _source: 'social-reel' } }] }),
        });
        if (!response.ok) throw new Error(String(response.status));
        // Feature statistics (assets/liore-track.js); a no-op without consent.
        window.lioreTrack?.('social_add_to_cart', { product: form.dataset.statProduct || '' });

        // Confirm in place and leave the video running: someone who adds
        // from a video is usually not finished watching it.
        form.hidden = true;
        // A previous failure's message otherwise stayed on screen next to the
        // success confirmation, since only #stop() cleared it.
        const stale = scope?.querySelector('[data-social-error]');
        if (stale) stale.hidden = true;
        const added = scope?.querySelector('[data-social-added]');
        if (added) added.hidden = false;
        const cartLink = scope?.querySelector('[data-social-cart-link]');
        if (cartLink) cartLink.hidden = false;
        // The theme's listeners read `event.promise` and `event.action`, not
        // `event.detail` — see cart-icon.js (`event.promise?.then(({cart,
        // detail}) => …)`) and cart-drawer.js (`event.action === 'add'`).
        // A bare CustomEvent is dispatched but every consumer no-ops on it.
        const cartUrl = routes.cart_url || '/cart';
        const update = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
        // `cart-drawer.js` auto-opens on `action === 'add'`, and that drawer
        // is a top-layer modal painted over the whole viewport — an overlay
        // obscuring the player, which the RMF forbids outright, and the
        // opposite of leaving the video watchable. The bubble and the live
        // region both read `event.promise` and ignore `action`, so a
        // non-'add' action keeps the count correct without the overlay.
        update.action = this.#playing ? 'update' : 'add';
        update.context = 'product';
        update.promise = fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => ({
            cart: { ...cart, totalQuantity: cart.item_count },
            // `source` lets product-form.js skip a redundant #refreshCart().
            detail: { itemCount: cart.item_count, source: 'liore-social-rail' },
          }));
        document.dispatchEvent(update);
      } catch {
        button?.removeAttribute('disabled');
        const error = scope?.querySelector('[data-social-error]');
        if (error) error.hidden = false;
      }
    };

    #onEnter = (event) => {
      const card = event.target.closest?.('[data-social-card]');
      if (!card || card.dataset.playing !== undefined) return;

      const video = card.querySelector('[data-social-preview]');
      if (!video) return;

      const allowed = shouldPlayPreview({
        hasPreview: true,
        coarsePointer: matches('(pointer: coarse)'),
        reducedMotion: matches('(prefers-reduced-motion: reduce)'),
      });
      if (!allowed) return;

      video.play?.().catch(() => {
        /* Autoplay refusal is not an error worth surfacing. */
      });
    };

    #onLeave = (event) => {
      const card = event.target.closest?.('[data-social-card]');
      if (!card) return;

      // `pointerleave` fires on every element the pointer exits, including
      // the overlaid capsule, play circle, duration pill and caption. Without
      // this the loop restarted from frame 0 whenever the cursor crossed one.
      if (event.relatedTarget && card.contains(event.relatedTarget)) return;

      const video = card.querySelector('[data-social-preview]');
      if (!video) return;
      video.pause?.();
      video.currentTime = 0;
    };
  }

  if (!customElements.get('liore-social-rail')) {
    customElements.define('liore-social-rail', LioreSocialRail);
  }
}
